import { AppThunk } from "../appStore"
import { setBooted, setClientId } from "../appSlice"
import { initFusionLdb } from "../../dal/ldb"
import { ldbMigrations } from "../../dal/ldbMigrations"
import { bootRestoreSettings } from "./restoreSettings"
import { pushLogEntry } from "../logSlice"
import { getApiDomain, getBuildNumber, getLongCopyright, getJsBundle, hasMinimumIosVersion, getIosVersion } from "../../lib/appInfo"
import { appSync } from "../syncthunk/syncThunk"
import { serializeFusionError, SettingId } from '@fusion-advantage/backcompat'
import { stringId, SyncStrategy } from "@fusion-advantage/formlib"
import { appError } from "../errorSlice"
import { initSQLitePlugin } from "../../plugins/sqlitePlugin"
import { SyncType } from "../syncthunk/SyncConfig"
import { App as CapacitorApp } from '@capacitor/app'
import { appStateChange } from "./appStateChange"
import { selectSessionIsNotExpired } from "../selectSession"

interface appBootArgs {
	performInitialSync: boolean
}

export const appBoot = ({performInitialSync}: appBootArgs): AppThunk<Promise<void>> => async (dispatch, getState) => {
	if(getState().app.booted) {
		dispatch(appError({
			err: serializeFusionError('appBoot', 'already booted')
		}))
		return
	}
	dispatch(pushLogEntry('Fusion Advantage ••• App'))
	dispatch(pushLogEntry(`Build ${getBuildNumber() ?? '(web)'}`))
	dispatch(pushLogEntry(`Bundle ${getJsBundle() ?? '(unknown)'}`))
	dispatch(pushLogEntry(`Domain ${getApiDomain() ?? '(unknown)'}`))
	dispatch(pushLogEntry(getLongCopyright()))
	dispatch(pushLogEntry(`\u00A0`))
	if(!hasMinimumIosVersion()) {
		dispatch(appError({
			err: serializeFusionError('appBoot', `iosVersion '${getIosVersion()}' does not meet minimum requirements`),
			alertMessage: 'Your device software is not up to date.  Please update to the latest version of iOS.  If you need assistance, please contact support.',
			suppressDebugger: true,
		}))
		return
	}
	let r = await initFusionLdb()
	if(r.isErr()) {
		dispatch(appError({ err: r.error }))
		return
	}
	r = await ldbMigrations()
	if(r.isErr()) {
		dispatch(appError({ err: r.error }))
		// TODO
		return
	}
	r = await dispatch(bootRestoreSettings())
	if(r.isErr()) {
		dispatch(appError({ err: r.error }))
		// TODO
		return
	}
	r = await dispatch(initSQLitePlugin())
	if(r.isErr()) {
		dispatch(appError({ err: r.error }))
		return
	}
	await CapacitorApp.addListener('appStateChange', ({isActive}) => {
		dispatch(appStateChange({isActive}))
	})
	dispatch(setBooted())
	// generate and store new client id if not present
	if(!getState().app[SettingId.ClientId] || getState().app[SettingId.ClientId] == '') {
		dispatch(setClientId(stringId(40)))
	}
	dispatch(pushLogEntry(
		`[appBoot] client id is ${getState().app[SettingId.ClientId]}`
	))
	// for developer debugging/experimentation only (not for release):
	// uncomment here temporarily to perform experimental tests for
	// @capacitor-community/sqlite on app startup;
	// see also LdbBlobData.ts
	// await dispatch(runCapacitorSqlRuntimeTests())
	// TODO for print mode??
	// ---
	if(performInitialSync) {
		let bootSyncType = SyncType.Boot
		// in the situation we have a mid-day app restart where the stored token
		// is likely still valid and offline tables have already been loaded,
		// we can try to sync less first to reduce no/low connectivity issues
		// impeding their work
		if(
			(getState().app.syncStrategy === SyncStrategy.Mobile) &&
			selectSessionIsNotExpired(getState())
		) {
			bootSyncType = SyncType.MobileReboot
		}
		await dispatch(appSync({
			type: bootSyncType,
		}))
	}
}
